import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from '@/shared/components/Tooltip'
import type { PolicyArtefactType, PolicyItem } from '@/lib/apiTypes'
import { useOrgPolicies } from '@/lib/hooks'
import { type Template } from '@/features/policies/lib/regoTemplates'
import { TemplateGallery } from '@/features/policies/components/TemplateGallery'
import { BaselinePolicyModal, baselineToast } from '@/features/policies/components/BaselinePolicyModal'
import { api } from '@/lib/api'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonCard } from '@/shared/components/Skeleton'
import { useToast } from '@/context/ToastContext'
import { ComplianceTooltip, SCOPE_PLURAL } from '@/features/policies/components/ComplianceTooltip'
import { PolicyModal } from '@/features/policies/components/PolicyModal'
import { rescanNote } from '@/features/policies/lib/rescanNote'

/** Full policy CRUD list with create/edit modal and per-policy toggle/enforce/delete
 *  actions, scoped to a single artefact tab. */
export function PolicyList({ orgId, canAdmin, isOwner, artefactType }: { orgId: string; canAdmin: boolean; isOwner: boolean; artefactType: PolicyArtefactType }) {
  const { data, loading, error, refetch } = useOrgPolicies(orgId, artefactType)
  const { showToast } = useToast()
  const [showModal, setShowModal]         = useState(false)
  const [editPolicy, setEditPolicy]       = useState<PolicyItem | null>(null)
  const [confirmDeletePolicy, setConfirmDeletePolicy] = useState<PolicyItem | null>(null)
  const [busy, setBusy]                   = useState<string | null>(null)
  const [gallery, setGallery]             = useState(false)
  // A template picked in the gallery, which then opens the create form already filled in. Same
  // two-step as the Policies overview, except the artefact is already decided by the tab.
  const [picked, setPicked]               = useState<Template | null>(null)
  const [baseline, setBaseline]           = useState(false)

  function withFeedback(msg: string, ok = true) {
    showToast(msg, ok ? 'success' : 'error')
  }

  async function handleToggle(policy: PolicyItem) {
    setBusy(policy.id + '-toggle')
    try {
      const res = await api.post<{ rescan?: string }>(`/api/orgs/${orgId}/policies/${policy.id}/toggle`, {})
      refetch()
      withFeedback(`Policy ${policy.is_enabled ? 'disabled' : 'enabled'}.${rescanNote(res.rescan)}`)
    } catch (err) {
      withFeedback((err as Error).message, false)
    } finally {
      setBusy(null)
    }
  }

  async function handleToggleEnforcing(policy: PolicyItem) {
    setBusy(policy.id + '-enforce')
    try {
      const res = await api.post<{ rescan?: string }>(`/api/orgs/${orgId}/policies/${policy.id}/toggle-enforcing`, {})
      refetch()
      withFeedback(`Policy set to ${policy.is_enforcing ? 'advisory' : 'enforcing'}.${rescanNote(res.rescan)}`)
    } catch (err) {
      withFeedback((err as Error).message, false)
    } finally {
      setBusy(null)
    }
  }

  async function handleDelete(id: string) {
    setConfirmDeletePolicy(null)
    setBusy(id + '-del')
    try {
      await api.delete(`/api/orgs/${orgId}/policies/${id}`)
      refetch()
      withFeedback('Policy deleted.')
    } catch (err) {
      withFeedback((err as Error).message, false)
    } finally {
      setBusy(null)
    }
  }

  // The Danger-zone kill switch is pinned first and read-only below: it is the loudest thing
  // acting on this artefact type, so burying it in created_at order would hide the reason
  // everything is being refused.
  const policies = [...(data?.policies ?? [])]
    .sort((a, b) => (b.is_block_all ?? 0) - (a.is_block_all ?? 0))

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {(showModal || editPolicy || picked) && (
        <PolicyModal
          orgId={orgId}
          policy={editPolicy ?? undefined}
          artefactType={artefactType}
          template={picked ?? undefined}
          onClose={() => { setShowModal(false); setEditPolicy(null); setPicked(null) }}
          onSaved={(rescan) => { setShowModal(false); setEditPolicy(null); setPicked(null); refetch(); withFeedback(`Policy saved.${rescanNote(rescan)}`) }}
        />
      )}

      {gallery && (
        // Scoped to this tab's artefact: a template only reads fields that exist on its own
        // artefact, and here the artefact is already decided.
        <TemplateGallery
          artefactType={artefactType}
          onPick={(template) => setPicked(template)}
          onClose={() => setGallery(false)}
        />
      )}

      {baseline && (
        <BaselinePolicyModal
          orgId={orgId}
          artefactType={artefactType}
          existing={policies}
          onClose={() => setBaseline(false)}
          onApplied={(result) => { setBaseline(false); refetch(); withFeedback(baselineToast(result, rescanNote)) }}
        />
      )}

      {confirmDeletePolicy && (
        <ConfirmModal
          track="org_policy_delete_confirm"
          title="Delete policy"
          message={`Delete policy "${confirmDeletePolicy.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          busy={busy === confirmDeletePolicy.id + '-del'}
          onConfirm={() => handleDelete(confirmDeletePolicy.id)}
          onCancel={() => setConfirmDeletePolicy(null)}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)', letterSpacing: '0.08em' }}>
          // {policies.length} org-level polic{policies.length !== 1 ? 'ies' : 'y'}
        </div>
        {canAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setGallery(true)}>
              Browse templates
            </button>
            {/* The one-press route: a curated set, applied enabled, deduped against what is
                already here. Ghost rather than primary - "+ New policy" stays the page's
                primary action, this is the shortcut past it. */}
            <button className="btn btn-ghost btn-sm" onClick={() => setBaseline(true)}>
              Add recommended
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
              + New policy
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div style={{ display: 'grid', gap: 10 }} aria-hidden="true">
          {Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} header={false} rows={2} />)}
        </div>
      )}

      {!loading && error && (
        error.toLowerCase().includes('forbidden') || error.toLowerCase().includes('not authorized') || error.toLowerCase().includes('permission')
          ? <EmptyState icon="🔒" title="Access restricted" subtitle="You don't have permission to view policies. Ask an org owner or admin to grant you access." />
          : <EmptyState icon="⚠" title="Failed to load policies" subtitle={error} />
      )}

      {!loading && !error && policies.length === 0 && (
        <EmptyState icon="⬡" title="No org-level policies yet." />
      )}
      {!loading && !error && policies.length === 0 && canAdmin && (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(true)}>
            Create your first policy
          </button>
        </div>
      )}

      {!loading && !error && policies.length > 0 && (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>
                  <Tooltip text="The policy's name and description. Click a row to edit it." placement="top">
                    <span tabIndex={0} className="cursor-help">Policy</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Enforcing blocks what fails the policy; advisory only reports it." placement="top">
                    <span tabIndex={0} className="cursor-help">Enforcement</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Whether the policy is evaluated at all." placement="top">
                    <span tabIndex={0} className="cursor-help">Status</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Failing over evaluated artefacts at the policy's scope. Hover for which ones fail." placement="top">
                    <span tabIndex={0} className="cursor-help">Compliance</span>
                  </Tooltip>
                </th>
                {canAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr
                  key={p.id}
                  id={`policy-${p.id}`}
                  onClick={canAdmin && !p.is_block_all ? () => setEditPolicy(p) : undefined}
                  style={canAdmin && !p.is_block_all ? { cursor: 'pointer' } : undefined}
                  title={
                    p.is_block_all ? 'Managed by the organisation Danger zone'
                    : canAdmin ? 'Edit policy' : undefined
                  }
                >
                  <td>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text)', fontWeight: 500 }}>
                      {p.name}
                      {!!p.is_block_all && (
                        <span className="badge badge-red" style={{ marginLeft: 8, fontSize: '0.65rem' }}>DANGER ZONE</span>
                      )}
                    </div>
                    {p.description && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text2)', marginTop: 2 }}>{p.description}</div>
                    )}
                  </td>
                  <td>
                    <span className={p.is_enforcing ? 'badge badge-red' : 'badge badge-amber'} style={{ fontSize: '0.65rem' }}>
                      {p.is_enforcing ? 'enforcing' : 'advisory'}
                    </span>
                  </td>
                  <td>
                    <span className={p.is_enabled ? 'badge badge-green' : 'badge badge-neutral'} style={{ fontSize: '0.65rem' }}>
                      {p.is_enabled ? 'enabled' : 'disabled'}
                    </span>
                  </td>
                  <td>
                    {p.eval_total === undefined ? (
                      <span style={{ color: 'var(--text3)', fontSize: '0.7rem' }}>—</span>
                    ) : (
                      <Tooltip
                        placement="top"
                        interactive
                        text={
                          <ComplianceTooltip
                            total={p.eval_total ?? 0}
                            failing={p.eval_failing ?? 0}
                            names={p.eval_failing_names}
                            scope={SCOPE_PLURAL[p.artefact_type]}
                          />
                        }
                      >
                        <span
                          tabIndex={0}
                          className={(p.eval_failing ?? 0) > 0 ? 'badge badge-red' : ((p.eval_total ?? 0) > 0 ? 'badge badge-green' : 'badge badge-neutral')}
                          style={{ fontSize: '0.65rem', cursor: 'default' }}
                        >
                          {(p.eval_total ?? 0) === 0 ? 'no data' : `${p.eval_failing ?? 0} / ${p.eval_total}`}
                        </span>
                      </Tooltip>
                    )}
                  </td>
                  {canAdmin && (
                    // Row-level actions; stopPropagation so they don't also open the edit modal.
                    <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {/* The generic toggle/edit/delete endpoints answer 409 for this row, so
                            offering the buttons would only produce an error. The Danger zone is
                            owner-only, hence a link rather than a control. */}
                        {!!p.is_block_all && (
                          <Link to={`/org/${orgId}/settings?tab=members`} className="btn btn-ghost btn-sm">
                            Manage in Danger zone
                          </Link>
                        )}
                        {!p.is_block_all && (
                        <>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy === p.id + '-toggle'}
                          onClick={() => handleToggle(p)}
                        >
                          {busy === p.id + '-toggle' ? <span className="spinner" /> : (p.is_enabled ? 'Disable' : 'Enable')}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy === p.id + '-enforce'}
                          onClick={() => handleToggleEnforcing(p)}
                        >
                          {busy === p.id + '-enforce' ? <span className="spinner" /> : (p.is_enforcing ? 'Set advisory' : 'Set enforcing')}
                        </button>
                        {isOwner && (
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeletePolicy(p)}>Delete</button>
                        )}
                        </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
